import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { shiftService } from '@/api/domains/shift';
import type { LC_BulkApproveShiftRequestsOut } from '@/api/domains/shift/shift.types';
import { useUI } from '@/hooks/ui/useUI';


interface UseShiftChangeRequestBulkApprovalParams {
  selectedRequestIds: number[];
  onSuccess: (result: LC_BulkApproveShiftRequestsOut) => void;
}

export interface UseShiftChangeRequestBulkApprovalResult {
  comment: string;
  setComment: (value: string) => void;
  canSubmit: boolean;
  isSubmitting: boolean;
  handleSubmit: () => Promise<void>;
}

/**
 * Lógica para la aprobación masiva de solicitudes de cambio de turno (ASIS-5235).
 * Envía los IDs seleccionados como CSV y un comentario obligatorio al endpoint
 * PUT /v1/shift/change-requests/bulk-approval.
 */
export function useShiftChangeRequestBulkApproval({
  selectedRequestIds,
  onSuccess,
}: UseShiftChangeRequestBulkApprovalParams): UseShiftChangeRequestBulkApprovalResult {
  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, showWarning } = useUI();

  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const canSubmit = comment.trim().length > 0 && !isSubmitting;

  const handleSubmit = useCallback(async () => {
    if (submittingRef.current || comment.trim().length === 0) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const response = await shiftService.bulkApproveShiftRequests({
        requestIds: selectedRequestIds.join(','),
        approverComment: comment.trim(),
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:shiftChangeAdmin.messages.approveError'));
        throw new Error('bulk-approve-failed');
      }

      const result = response.data;

      if (result.totalAlreadyResolved === 0) {
        showSuccess(translate('attendance:shiftChangeAdmin.bulk.approveSuccess'));
      } else {
        showWarning(
          translate('attendance:shiftChangeAdmin.bulk.partialResult', {
            processed: result.totalApproved,
            skipped: result.totalAlreadyResolved,
          }),
        );
      }

      onSuccess(result);
      setComment('');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    comment,
    selectedRequestIds,
    showApiError,
    showSuccess,
    showWarning,
    translate,
    onSuccess,
  ]);

  return {
    comment,
    setComment,
    canSubmit,
    isSubmitting,
    handleSubmit,
  };
}

export default useShiftChangeRequestBulkApproval;
